import type { Channel, Server } from "stoat.js";

const THREAD_METADATA_PREFIX = "<!-- outpost-thread:";
const THREAD_METADATA_SUFFIX = " -->";

export interface ThreadMetadata {
  version: 1;
  parentChannelId: string;
  rootMessageId: string;
  createdFromChannelId: string;
}

export function parseThreadMetadata(
  description?: string,
): ThreadMetadata | undefined {
  if (!description?.startsWith(THREAD_METADATA_PREFIX)) return undefined;

  const endIndex = description.indexOf(THREAD_METADATA_SUFFIX);
  if (endIndex === -1) return undefined;

  try {
    const raw = description.slice(THREAD_METADATA_PREFIX.length, endIndex);
    const parsed = JSON.parse(raw) as Partial<ThreadMetadata>;

    if (
      parsed.version !== 1 ||
      typeof parsed.parentChannelId !== "string" ||
      typeof parsed.rootMessageId !== "string" ||
      typeof parsed.createdFromChannelId !== "string"
    ) {
      return undefined;
    }

    return parsed as ThreadMetadata;
  } catch {
    return undefined;
  }
}

export function getVisibleChannelDescription(
  description?: string,
): string | undefined {
  if (!description) return undefined;

  const endIndex = description.indexOf(THREAD_METADATA_SUFFIX);
  if (!description.startsWith(THREAD_METADATA_PREFIX) || endIndex === -1) {
    return description;
  }

  const visible = description
    .slice(endIndex + THREAD_METADATA_SUFFIX.length)
    .replace(/^\r?\n/, "")
    .trim();

  return visible || undefined;
}

export function buildThreadDescription(
  metadata: ThreadMetadata,
  visibleDescription?: string,
): string {
  const payload = `${THREAD_METADATA_PREFIX}${JSON.stringify(metadata)}${THREAD_METADATA_SUFFIX}`;
  const visible = visibleDescription?.trim();

  return visible ? `${payload}\n${visible}` : payload;
}

export function updateChannelDescriptionWithThreadMetadata(
  channel: Channel,
  visibleDescription?: string,
): string | undefined {
  const metadata = parseThreadMetadata(channel.description);
  if (!metadata) {
    return visibleDescription?.trim() || undefined;
  }

  return buildThreadDescription(metadata, visibleDescription);
}

export function getThreadParentId(channel: Channel): string | undefined {
  return parseThreadMetadata(channel.description)?.parentChannelId;
}

export function isThreadChannel(channel: Channel): boolean {
  return Boolean(getThreadParentId(channel));
}

export function getThreadOwnerChannel(channel: Channel): Channel {
  const parentChannelId = getThreadParentId(channel);
  return parentChannelId
    ? (channel.server?.channels.find((entry) => entry.id === parentChannelId) ??
        channel)
    : channel;
}

export function getOrderedServerChannels(server: Server): Channel[] {
  return server.orderedChannels.flatMap((category) => category.channels);
}

export function getThreadChannelsForParent(
  server: Server,
  parentChannelId: string,
): Channel[] {
  return getOrderedServerChannels(server).filter(
    (channel) => getThreadParentId(channel) === parentChannelId,
  );
}
